import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { SegmentConstraintIntersection } from "building-types/framing/floorFramingConstraintTypes";
import type { JoistRayOrigin } from "building-types/framing/floorFramingTypes";
import type { FramingOpeningRange } from "building-types/framing/framingLayoutTypes";
import type { BoundaryVertex } from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import { makeFramingProfile2D } from "doc/utils/lumberTools";
import type { Face } from "geom/brep";
import { clipFacesWithPlanes } from "geom/bspClipping";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { CurveCurvePointIntersection, intersectLineSegments2D } from "geom/intersectLine2Line2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Copy, polyLine3ExtendBothEnds, polyLine3ExtendEnd, polyLine3ExtendStart } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2Dist } from "geom/vec2";
import { computeProjectedDistance } from "../framingLayoutUtils";

const SDL_WIDTH = 1.75;

/**
 * Joist ray origins along the spacing axis (opening trimmers/tail typing applied after the grid).
 *
 * Intentionally does **not** call {@link generateFramingSpacingOriginPoints}: floor `framingStartPt`
 * often lies inside the bay (floor systems, bays, mirrors). Spacing is anchored from the min-perp
 * side and stepped forward to max-perp so the full plate gets joists regardless of start position.
 */
export function generateJoistRayOriginPoints(
  sortedVertices: BoundaryVertex[],
  spacing: number,
  startPoint: Vec2,
  framingDirection: Vec2,
  perpDirection: Vec2,
  openingRanges: FramingOpeningRange[],
  includeStart: boolean = true,
  includeEnds: boolean = true,
  forceTrimmersAtOpeningEnds: boolean = false,
  tailJoistDisabled: boolean = false,
  trimmerDisabled: boolean = false,
  trimmerCount: number = 1,
  trimmerWidth: number = SDL_WIDTH,
): JoistRayOrigin[] {
  if (sortedVertices.length === 0) {
    return [];
  }

  const BIG_NUMBER = 1e6;
  const unfilteredOriginPoints: Omit<
    JoistRayOrigin,
    "joistType" | "sourceOpeningIndices" | "trimmerLocations" | "trimmerIndex"
  >[] = [];

  const minPerpDist = sortedVertices[0].perpDistance;
  const maxPerpDist = sortedVertices[sortedVertices.length - 1].perpDistance;
  const startPerpDist = computeProjectedDistance(startPoint, startPoint, perpDirection);

  const baseX = startPoint[0] - BIG_NUMBER * framingDirection[0];
  const baseY = startPoint[1] - BIG_NUMBER * framingDirection[1];

  const startOffset = Math.ceil((minPerpDist - startPerpDist) / spacing) * spacing;
  let currentPerpDist = startPerpDist + startOffset;

  if (includeStart && startPerpDist >= minPerpDist - DIST_EPSILON && startPerpDist <= maxPerpDist + DIST_EPSILON) {
    const x = baseX + startPerpDist * perpDirection[0];
    const y = baseY + startPerpDist * perpDirection[1];
    unfilteredOriginPoints.push({ x, y, perpDistance: startPerpDist });
  }

  if (includeEnds) {
    const minX = baseX + minPerpDist * perpDirection[0];
    const minY = baseY + minPerpDist * perpDirection[1];
    const isDuplicateMin = unfilteredOriginPoints.some(
      (p) => Math.abs(p.x - minX) < DIST_EPSILON && Math.abs(p.y - minY) < DIST_EPSILON,
    );
    if (!isDuplicateMin) {
      unfilteredOriginPoints.push({ x: minX, y: minY, perpDistance: minPerpDist });
    }

    const maxX = baseX + maxPerpDist * perpDirection[0];
    const maxY = baseY + maxPerpDist * perpDirection[1];
    const isDuplicateMax = unfilteredOriginPoints.some(
      (p) => Math.abs(p.x - maxX) < DIST_EPSILON && Math.abs(p.y - maxY) < DIST_EPSILON,
    );
    if (!isDuplicateMax) {
      unfilteredOriginPoints.push({ x: maxX, y: maxY, perpDistance: maxPerpDist });
    }
  }

  while (currentPerpDist <= maxPerpDist + DIST_EPSILON) {
    const isDuplicate = unfilteredOriginPoints.some((p) => Math.abs(p.perpDistance - currentPerpDist) < DIST_EPSILON);
    if (!isDuplicate) {
      const x = baseX + currentPerpDist * perpDirection[0];
      const y = baseY + currentPerpDist * perpDirection[1];
      unfilteredOriginPoints.push({ x, y, perpDistance: currentPerpDist });
    }
    currentPerpDist += spacing;
  }

  if (forceTrimmersAtOpeningEnds) {
    for (const range of openingRanges) {
      const minX = baseX + range.minDist * perpDirection[0];
      const minY = baseY + range.minDist * perpDirection[1];
      const isDuplicateMin = unfilteredOriginPoints.some(
        (p) => Math.abs(p.perpDistance - range.minDist) < DIST_EPSILON,
      );
      if (!isDuplicateMin) {
        unfilteredOriginPoints.push({
          x: minX,
          y: minY,
          perpDistance: range.minDist,
        });
      }

      const maxX = baseX + range.maxDist * perpDirection[0];
      const maxY = baseY + range.maxDist * perpDirection[1];
      const isDuplicateMax = unfilteredOriginPoints.some(
        (p) => Math.abs(p.perpDistance - range.maxDist) < DIST_EPSILON,
      );
      if (!isDuplicateMax) {
        unfilteredOriginPoints.push({
          x: maxX,
          y: maxY,
          perpDistance: range.maxDist,
        });
      }
    }
  }

  unfilteredOriginPoints.sort((a, b) => a.perpDistance - b.perpDistance);

  const getJoistTypeAndSourceOpeningInfo = (
    perpDist: number,
  ): {
    joistType: FloorFramingComponentType;
    sourceOpeningIndices?: number[];
    trimmerLocations?: ("start" | "end")[];
  } => {
    const matchedOpenings: { openingIndex: number; trimmerLocation: "start" | "end" }[] = [];

    for (const range of openingRanges) {
      const distToMin = range.minDist - perpDist;
      const distToMax = perpDist - range.maxDist;
      const isNearEnd = (dist: number) => Math.abs(dist) < DIST_EPSILON;

      if (isNearEnd(distToMin)) {
        matchedOpenings.push({ openingIndex: range.openingIndex, trimmerLocation: "start" });
      }
      if (isNearEnd(distToMax)) {
        matchedOpenings.push({ openingIndex: range.openingIndex, trimmerLocation: "end" });
      }
    }

    if (matchedOpenings.length > 0) {
      return {
        joistType: FloorFramingComponentType.TRIMMER,
        sourceOpeningIndices: matchedOpenings.map((m) => m.openingIndex),
        trimmerLocations: matchedOpenings.map((m) => m.trimmerLocation),
      };
    }

    for (const range of openingRanges) {
      if (perpDist > range.minDist + DIST_EPSILON && perpDist < range.maxDist - DIST_EPSILON) {
        return { joistType: FloorFramingComponentType.TAIL_JOIST, sourceOpeningIndices: [range.openingIndex] };
      }
    }

    if (!forceTrimmersAtOpeningEnds) {
      for (const range of openingRanges) {
        const distToMin = range.minDist - perpDist;
        const distToMax = perpDist - range.maxDist;
        const isNearEnd = (dist: number) => dist > 0 && dist < spacing - DIST_EPSILON;

        if (isNearEnd(distToMin)) {
          matchedOpenings.push({ openingIndex: range.openingIndex, trimmerLocation: "start" });
        }
        if (isNearEnd(distToMax)) {
          matchedOpenings.push({ openingIndex: range.openingIndex, trimmerLocation: "end" });
        }
      }

      if (matchedOpenings.length > 0) {
        return {
          joistType: FloorFramingComponentType.TRIMMER,
          sourceOpeningIndices: matchedOpenings.map((m) => m.openingIndex),
          trimmerLocations: matchedOpenings.map((m) => m.trimmerLocation),
        };
      }
    }

    return { joistType: FloorFramingComponentType.FLOOR_JOIST };
  };

  const finalOriginPoints: JoistRayOrigin[] = [];

  for (let i = 0; i < unfilteredOriginPoints.length; i++) {
    const point = unfilteredOriginPoints[i];
    const joistTypeAndSourceOpeningInfo = getJoistTypeAndSourceOpeningInfo(point.perpDistance);
    if (tailJoistDisabled && joistTypeAndSourceOpeningInfo.joistType === FloorFramingComponentType.TAIL_JOIST) {
      continue;
    }
    if (trimmerDisabled && joistTypeAndSourceOpeningInfo.joistType === FloorFramingComponentType.TRIMMER) {
      continue;
    }

    if (joistTypeAndSourceOpeningInfo.joistType === FloorFramingComponentType.TRIMMER) {
      const hasEndLocation = joistTypeAndSourceOpeningInfo.trimmerLocations?.includes("end") ?? false;
      const endOffset = hasEndLocation ? trimmerWidth * trimmerCount : 0;
      for (let j = 0; j < trimmerCount; j++) {
        const newPerpDistance = point.perpDistance - trimmerWidth * j + endOffset;
        finalOriginPoints.push({
          x: baseX + newPerpDistance * perpDirection[0],
          y: baseY + newPerpDistance * perpDirection[1],
          perpDistance: newPerpDistance,
          joistType: FloorFramingComponentType.TRIMMER,
          sourceOpeningIndices: joistTypeAndSourceOpeningInfo.sourceOpeningIndices,
          trimmerLocations: joistTypeAndSourceOpeningInfo.trimmerLocations,
          trimmerIndex: j,
        });
      }
    } else {
      finalOriginPoints.push({
        ...point,
        ...joistTypeAndSourceOpeningInfo,
      });
    }
  }
  return finalOriginPoints.sort((a, b) => a.perpDistance - b.perpDistance);
}

export function findSegmentConstraintIntersections(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  constraintPolyline: PolyLine2,
): SegmentConstraintIntersection[] {
  const intersections: SegmentConstraintIntersection[] = [];

  const segLen = vec2Dist(x1, y1, x2, y2);
  if (segLen * segLen < DIST_EPSILON_SQUARED) {
    return intersections;
  }

  for (let i = 0; i < constraintPolyline.length - 2; i += 2) {
    const cx1 = constraintPolyline[i];
    const cy1 = constraintPolyline[i + 1];
    const cx2 = constraintPolyline[i + 2];
    const cy2 = constraintPolyline[i + 3];

    const result = intersectLineSegments2D(x1, y1, x2, y2, cx1, cy1, cx2, cy2);

    if (result && result instanceof CurveCurvePointIntersection) {
      const t = result.param0 / segLen;

      intersections.push({
        x: result.x,
        y: result.y,
        t: Math.max(0, Math.min(1, t)),
        constraintEdgeIndex: Math.floor(i / 2),
      });
    }
  }

  intersections.sort((a, b) => a.t - b.t);

  return intersections;
}

export function computeAngleAdjustedOffset(
  constraintWidth: number,
  joistDirX: number,
  joistDirY: number,
  constraintDirX: number,
  constraintDirY: number,
): number {
  const joistLen = Math.sqrt(joistDirX * joistDirX + joistDirY * joistDirY);
  const constraintLen = Math.sqrt(constraintDirX * constraintDirX + constraintDirY * constraintDirY);

  if (joistLen < DIST_EPSILON || constraintLen < DIST_EPSILON) {
    return 0.5 * constraintWidth;
  }

  const nJoistX = joistDirX / joistLen;
  const nJoistY = joistDirY / joistLen;
  const nConstraintX = constraintDirX / constraintLen;
  const nConstraintY = constraintDirY / constraintLen;

  const cross = Math.abs(nJoistX * nConstraintY - nJoistY * nConstraintX);
  const sinAngle = Math.max(cross, 0.1);

  return (0.5 * constraintWidth) / sinAngle;
}

export function getConstraintEdgeDirection(constraintPolyline: PolyLine2, edgeIndex: number): Vec2 | null {
  const vertexIndex = edgeIndex * 2;
  if (vertexIndex + 3 >= constraintPolyline.length) {
    return null;
  }

  const x1 = constraintPolyline[vertexIndex];
  const y1 = constraintPolyline[vertexIndex + 1];
  const x2 = constraintPolyline[vertexIndex + 2];
  const y2 = constraintPolyline[vertexIndex + 3];

  return [x2 - x1, y2 - y1];
}

export function floorFramingFaces(lumberRail: LumberRail, profileDescriptor: FramingProfileDescriptor): Face[] {
  const profile = [makeFramingProfile2D(profileDescriptor)];

  const { rail, nX, nY, nZ, clipPlanes, startExtension, endExtension } = lumberRail;
  let railExt = polyLine3Copy(rail);
  if (startExtension && endExtension) {
    railExt = polyLine3ExtendBothEnds(railExt, startExtension, endExtension);
  } else if (startExtension) {
    railExt = polyLine3ExtendStart(railExt, startExtension);
  } else if (endExtension) {
    railExt = polyLine3ExtendEnd(railExt, endExtension);
  }
  let faces = extrudeFaceAlongPolyLine3(profile, railExt, nX, nY, nZ).faces;

  if (clipPlanes && clipPlanes.length > 0) {
    faces = clipFacesWithPlanes({
      faces,
      planes: [...clipPlanes],
      addCapFaces: true,
    });
  }

  return faces;
}
